/**
 * Responsive utility functions for handling screen size calculations
 * Includes zoom-aware calculations for Electron apps
 */

export const BREAKPOINTS = {
  xs: 320,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
  '3xl': 1920
};

/**
 * Get current zoom level (Electron only)
 * @returns {number} Current zoom level (0 = 100%, -1 = ~90%, 1 = ~110%)
 */
export const getCurrentZoomLevel = () => {
  if (typeof window !== 'undefined' && window.require) {
    try {
      const { webFrame } = window.require('electron');
      return webFrame.getZoomLevel();
    } catch (error) {
      console.log('Not in Electron environment');
    }
  }
  return 0; // Default to 100% zoom
};

/**
 * Convert zoom level to scale factor
 * @param {number} zoomLevel - Electron zoom level
 * @returns {number} Scale factor (1.0 = 100%, 0.69 = 69%)
 */
export const zoomLevelToScale = (zoomLevel = 0) => {
  return Math.pow(1.2, zoomLevel);
};

/**
 * Get normalized (zoom-adjusted) viewport dimensions
 * @returns {object} { width, height, zoomLevel, scaleFactor }
 */
export const getNormalizedViewportSize = () => {
  const zoomLevel = getCurrentZoomLevel();
  const scaleFactor = zoomLevelToScale(zoomLevel);
  
  return {
    width: Math.round(window.innerWidth / scaleFactor),
    height: Math.round(window.innerHeight / scaleFactor),
    zoomLevel,
    scaleFactor,
    zoomedWidth: window.innerWidth,
    zoomedHeight: window.innerHeight
  };
};

/**
 * Get current screen breakpoint using normalized dimensions
 * @param {boolean} useNormalized - Whether to use zoom-normalized dimensions
 * @returns {string} Current breakpoint name
 */
export const getCurrentBreakpoint = (useNormalized = true) => {
  const width = useNormalized ? getNormalizedViewportSize().width : window.innerWidth;
  
  if (width >= BREAKPOINTS['3xl']) return '3xl';
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
};

/**
 * Get optimal column count based on screen size and max columns
 * @param {number} maxColumns - Maximum desired columns
 * @returns {number} Optimal column count
 */
export const getOptimalColumnCount = (maxColumns = 4) => {
  const breakpoint = getCurrentBreakpoint();
  
  switch (breakpoint) {
    case '3xl':
      return Math.min(maxColumns, 6);
    case '2xl':
      return Math.min(maxColumns, 5);
    case 'xl':
      return Math.min(maxColumns, 4);
    case 'lg':
      return Math.min(maxColumns, 4);
    case 'md':
      return Math.min(maxColumns, 3);
    case 'sm':
      return Math.min(maxColumns, 2);
    case 'xs':
    default:
      return 1;
  }
};

/**
 * Get responsive grid classes for CSS Grid
 * @param {number} maxColumns - Maximum desired columns
 * @returns {string} Tailwind CSS grid classes
 */
export const getResponsiveGridClasses = (maxColumns = 4) => {
  const cols = Math.min(maxColumns, 6);
  
  switch (cols) {
    case 1:
      return "grid-cols-1";
    case 2:
      return "grid-cols-1 xs:grid-cols-1 sm:grid-cols-2";
    case 3:
      return "grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3";
    case 4:
      return "grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
    case 5:
      return "grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";
    case 6:
    default:
      return "grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6";
  }
};

/**
 * Get responsive masonry breakpoints
 * @param {number} maxColumns - Maximum desired columns
 * @returns {object} Masonry breakpoint configuration
 */
export const getResponsiveMasonryBreakpoints = (maxColumns = 4) => {
  return {
    default: Math.min(maxColumns, 6),
    1920: Math.min(maxColumns, 6),
    1536: Math.min(maxColumns, 5),
    1280: Math.min(maxColumns, 4),
    1024: Math.min(maxColumns, 4),
    768: Math.min(maxColumns, 3),
    640: Math.min(maxColumns, 2),
    320: 1
  };
};

/**
 * Get responsive padding classes
 * @returns {string} Tailwind CSS padding classes
 */
export const getResponsivePadding = () => {
  return "px-2 sm:px-4 md:px-6 lg:px-8";
};

/**
 * Get responsive margin classes
 * @returns {string} Tailwind CSS margin classes
 */
export const getResponsiveMargin = () => {
  return "mx-2 sm:mx-4 md:mx-6 lg:mx-8";
};

/**
 * Get responsive text size classes
 * @param {string} baseSize - Base text size (e.g., 'text-lg')
 * @returns {string} Tailwind CSS text size classes
 */
export const getResponsiveTextSize = (baseSize = 'text-lg') => {
  const sizeMap = {
    'text-xs': 'text-xs sm:text-sm md:text-base',
    'text-sm': 'text-sm sm:text-base md:text-lg',
    'text-base': 'text-base sm:text-lg md:text-xl',
    'text-lg': 'text-lg sm:text-xl md:text-2xl',
    'text-xl': 'text-xl sm:text-2xl md:text-3xl',
    'text-2xl': 'text-2xl sm:text-3xl md:text-4xl'
  };
  
  return sizeMap[baseSize] || baseSize;
};

/**
 * Check if current screen is mobile using normalized dimensions
 * @param {boolean} useNormalized - Whether to use zoom-normalized dimensions
 * @returns {boolean} True if mobile screen size
 */
export const isMobile = (useNormalized = true) => {
  const width = useNormalized ? getNormalizedViewportSize().width : window.innerWidth;
  return width < BREAKPOINTS.md;
};

/**
 * Check if current screen is tablet using normalized dimensions
 * @param {boolean} useNormalized - Whether to use zoom-normalized dimensions
 * @returns {boolean} True if tablet screen size
 */
export const isTablet = (useNormalized = true) => {
  const width = useNormalized ? getNormalizedViewportSize().width : window.innerWidth;
  return width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
};

/**
 * Check if current screen is desktop using normalized dimensions
 * @param {boolean} useNormalized - Whether to use zoom-normalized dimensions
 * @returns {boolean} True if desktop screen size
 */
export const isDesktop = (useNormalized = true) => {
  const width = useNormalized ? getNormalizedViewportSize().width : window.innerWidth;
  return width >= BREAKPOINTS.lg;
};